import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from "recharts";
import { ExternalLink, AlertTriangle } from "lucide-react";
import { Section, MiniStat, EmptyState, chartTooltip } from "./Section";
import { DEFINITIONS, GROUP_META, fmtPct, fmtScore, fmtNum } from "./definitions";

const LowCsatList = ({ rows }) => (
  rows.length === 0 ? <EmptyState message="No 1–2 scores in this period." testId="low-csat-empty" /> : (
    <ul className="divide-y divide-slate-100" data-testid="low-csat-list">
      {rows.map((r) => (
        <li key={r.thread_id} className="py-2 flex items-start justify-between gap-3 text-sm" data-testid={`low-csat-${r.thread_id}`}>
          <div className="min-w-0">
            <p className="font-medium text-slate-800 truncate">{r.subject}</p>
            <p className="text-xs text-slate-500 truncate">{r.contact || "Unknown"} · {r.company} · {GROUP_META[r.group]?.label}</p>
            {r.comment && <p className="text-xs text-slate-600 italic mt-0.5 line-clamp-2">“{r.comment}”</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 text-xs font-semibold">{r.score}</span>
            {r.link && <a href={r.link} target="_blank" rel="noreferrer" className="text-[#425B76] hover:underline" data-testid={`low-csat-link-${r.thread_id}`}><ExternalLink className="w-4 h-4" /></a>}
          </div>
        </li>
      ))}
    </ul>
  )
);

export const QualitySection = ({ quality, csatStatus, target }) => {
  const groups = ["ai_resolved", "ai_assisted", "human_only"];
  const trend = quality.csat_trend.map((p) => ({ ...p, label: new Date(`${p.week}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) }));
  return (
    <Section title="Quality & satisfaction" testId="quality-section" info={[DEFINITIONS.csat, DEFINITIONS.reopen]}
      subtitle="CSAT by group, survey response and false-resolution checks">
      {csatStatus && !csatStatus.ok && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800" data-testid="csat-error">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {csatStatus.error}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {groups.map((g) => (
          <MiniStat key={g} label={`CSAT · ${GROUP_META[g].label}`} value={fmtScore(quality.csat_by_group[g].avg)}
            hint={`${fmtNum(quality.csat_by_group[g].count)} ratings`} testId={`csat-${g}`} />
        ))}
        <MiniStat label="Survey response rate" value={fmtPct(quality.survey_response_rate)} hint={`${fmtNum(quality.surveys)} surveys`} testId="stat-survey-rate" />
        <MiniStat label="Reopen / repeat (AI-resolved)" value={fmtPct(quality.reopen_rate_ai_resolved)} hint={`${fmtNum(quality.reopened_ai_resolved)} conversations`} testId="stat-reopen-rate" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">CSAT trend (weekly)</p>
          {trend.length === 0 ? <EmptyState message="No CSAT ratings matched to conversations yet." testId="csat-trend-empty" /> : (
            <div className="h-[220px]" data-testid="csat-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis domain={[1, 5]} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={chartTooltip} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={target} stroke="#e11d48" strokeDasharray="4 4" label={{ value: `Target ${target}`, fontSize: 10, fill: "#e11d48" }} />
                  {groups.map((g) => <Line key={g} dataKey={g} name={GROUP_META[g].label} stroke={GROUP_META[g].color} strokeWidth={2} connectNulls />)}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3" data-testid="thumbs-status">
            Thumbs up/down on bot messages: {quality.thumbs ? `${quality.thumbs.up} up / ${quality.thumbs.down} down` : "not captured by HubSpot for this inbox yet."}
          </p>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Recent low CSAT (1–2)</p>
          <LowCsatList rows={quality.low_csat} />
        </div>
      </div>
    </Section>
  );
};
